import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiSwitch from 'features/ui/base-components/pai-switch';
import PaiTextArea from 'features/ui/base-components/pai-text-area';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  id: string | undefined;
  isRefining: boolean;
  refinementFeedback: string;
  autoSave: boolean;

  editableProduct: string;
  editableRegion: string;
  editablePrompt: string;
  refineRegionSplitInstructions: string;
  refineSubsearchInstructions: string;
  peopleSearchInstructions: string;

  setEditableProduct: (product: string) => void;
  setEditableRegion: (region: string) => void;
  setEditablePrompt: (prompt: string) => void;
  setRefineRegionSplitInstructions: (instructions: string) => void;
  setRefineSubsearchInstructions: (instructions: string) => void;
  setPeopleSearchInstructions: (instructions: string) => void;

  setRefinementFeedback: (feedback: string) => void;
  setAutoSave: (autoSave: boolean) => void;

  handleCloseRefinementDialog: () => void;
  handleSubmitRefinement: () => void;
}

const RefineLeadListDialog: React.FC<Props> = ({
  open,
  id,
  isRefining,
  refinementFeedback,
  autoSave,
  editableProduct,
  editableRegion,
  editablePrompt,
  refineRegionSplitInstructions,
  refineSubsearchInstructions,
  peopleSearchInstructions,
  setEditableProduct,
  setEditableRegion,
  setEditablePrompt,
  setRefineRegionSplitInstructions,
  setRefineSubsearchInstructions,
  setPeopleSearchInstructions,
  setRefinementFeedback,
  setAutoSave,
  handleCloseRefinementDialog,
  handleSubmitRefinement,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth transitionDuration={0} maxWidth={'md'}>
      <DialogTitle className={classes.title}>
        <Box display="flex" alignItems="center" gap={1}>
          <RefreshIcon sx={{ color: '#FFF' }} />
          <Typography className={classes.titleText}>{t('refineLeadList')}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          <Alert className={classes.alert} severity="info">
            {t('refineLeadInfoAlert')}
          </Alert>
          <Alert className={classes.alert} severity="success">
            <Typography className={classes.label} fontWeight="bold" gutterBottom>
              {t('refinementFeatures')}
            </Typography>
            <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
              <li>{t('intelligentRefinement')}</li>
              <li>{t('preserveOriginalData')}</li>
              <li>{t('subregionalAnalysis')}</li>
              <li>{t('consistentFormatting')}</li>
              <li>{t('autoMergeLeads')}</li>
            </ul>
          </Alert>

          <Divider sx={{ my: 1 }} />

          <Typography className={classes.sectionTitle}>{t('researchParameters')}</Typography>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <PaiTextField
                label={t('product')}
                value={editableProduct}
                onChange={(e) => setEditableProduct(e.target.value)}
                placeholder={t('productPlaceholder')}
                variant={'white'}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <PaiTextField
                label={t('region')}
                value={editableRegion}
                onChange={(e) => setEditableRegion(e.target.value)}
                placeholder={t('regionPlaceholder')}
                variant={'white'}
              />
            </Grid>
            <Grid size={12}>
              <PaiTextArea
                label={t('searchPrompt')}
                value={editablePrompt}
                onChange={(e) => setEditablePrompt(e.target.value)}
                placeholder={t('searchPromptPlaceholder')}
                variant={'white'}
              />
            </Grid>
          </Grid>

          <PaiTextArea
            label={t('regionSplitInstruction')}
            value={refineRegionSplitInstructions}
            onChange={(e) => setRefineRegionSplitInstructions(e.target.value)}
            placeholder={t('regionSplitPlaceholder')}
            variant={'white'}
          />
          <PaiTextArea
            label={t('companyInstructions')}
            value={refineSubsearchInstructions}
            onChange={(e) => setRefineSubsearchInstructions(e.target.value)}
            placeholder={t('companySubSearchPlaceholder')}
            variant={'white'}
          />
          <PaiTextArea
            label={t('peopleSearchInstructions')}
            value={peopleSearchInstructions}
            onChange={(e) => setPeopleSearchInstructions(e.target.value)}
            placeholder={t('peopleSearchPlaceholder')}
            variant={'white'}
          />

          <Divider sx={{ my: 1 }} />

          <Typography className={classes.sectionTitle}>{t('leadTypePrompt')}</Typography>
          <PaiTextArea
            label={t('refinementFeedback')}
            value={refinementFeedback}
            onChange={(e) => setRefinementFeedback(e.target.value)}
            placeholder={t('refinementFeedbackPlaceholder')}
            variant={'white'}
          />

          <Divider sx={{ my: 1 }} />
          <Stack direction="row" alignItems="center" gap={1}>
            <PaiSwitch checked={autoSave} onChange={(e) => setAutoSave(e.target.checked)} />
            <Typography className={classes.label}>{id ? t('updateExistingList') : t('autoSaveResults')}</Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline="always" action={handleCloseRefinementDialog} />
        <PaiButton
          text={t('refineLeadList')}
          action={handleSubmitRefinement}
          size="small"
          border="rounded"
          disabled={!refinementFeedback.trim()}
          loading={isRefining}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  sectionTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  label: {
    fontSize: theme.typography.pxToRem(14),
  },
  alert: {
    margin: '0 !important',
    borderRadius: '8px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default RefineLeadListDialog;
